import { lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";

import { ago, POLL } from "@/lib/format";
import { assetData } from "@/api/client";
import { Donut } from "@/components/Donut";

// lazy: Leaflet is ~145KB and the map is the only thing on any page that
// needs it, so every other route keeps the pre-map bundle (same
// arrangement as the History replay).
const FleetMap = lazy(() => import("@/modules/asset_data/FleetMap")
  .then((m) => ({ default: m.FleetMap })));

const VER_COLORS = ["var(--teal)", "var(--info)", "var(--warn)", "var(--t-dim)", "var(--line2)"];


/** Live fleet health — who is reporting right now, on which firmware. Sourced
 *  from device_latest_state (the freshest snapshot per device). */
export function AssetDataOverviewPage() {
  const ov = useQuery({ queryKey: ["live", "overview"], queryFn: () => assetData.overview(), refetchInterval: POLL.DASHBOARD });
  const d = ov.data;
  const slices = d?.by_fw ?? [];
  const winMin = d ? Math.round(d.online_window_s / 60) : 5;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · Overview" title="Devices reporting now" />

      <div className="section-label">Where the fleet is</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <Suspense fallback={<div className="cap" style={{ height: 460 }}>Loading map…</div>}>
          <FleetMap />
        </Suspense>
      </div>

      <div className="section-label">Fleet liveness</div>
      <div className="livegrid" style={{ marginBottom: 16 }}>
        <div>
          <div className="k">Reporting</div>
          <div className="v num">{(d?.total ?? 0).toLocaleString("en-IN")}</div>
          <div className="s">devices with a snapshot</div>
        </div>
        <div>
          <div className="k">Online now</div>
          <div className="v num" style={{ color: "var(--ok)" }}>{(d?.online ?? 0).toLocaleString("en-IN")}</div>
          <div className="s">seen ≤ {winMin} min</div>
        </div>
        <div>
          <div className="k">Stale</div>
          <div className="v num">{(d?.stale ?? 0).toLocaleString("en-IN")}</div>
          <div className="s">silent &gt; {winMin} min</div>
        </div>
        <div>
          <div className="k">App versions</div>
          <div className="v num">{d?.app_versions ?? 0}</div>
          <div className="s">distinct firmware in field</div>
        </div>
        <div>
          <div className="k">Freshest report</div>
          <div className="v num" style={{ fontSize: 18 }}>{ago(d?.freshest ?? null)}</div>
          <div className="s">most recent beacon</div>
        </div>
      </div>

      <div className="section-label">Firmware / app version - devices in field</div>
      <div className="chartgrid">
        <div className="panel chart">
          <div className="ct">Devices by app version</div>
          <Donut
            slices={slices.map((s, i) => ({
              label: s.fw_ver ? s.fw_ver : "unknown",
              value: s.count,
              color: s.fw_ver ? VER_COLORS[i % VER_COLORS.length] : "var(--warn)",
              display: `${s.count.toLocaleString("en-IN")} dev`,
            }))}
          />
        </div>
      </div>

      {d && d.cross_company && d.by_company.length > 0 && (
        <>
          <div className="section-label">By company</div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr><th>Company</th><th>Company ID</th><th style={{ textAlign: "right" }}>Reporting</th></tr></thead>
              <tbody>
                {d.by_company.map((c) => (
                  <tr key={c.company_id}>
                    <td>{c.name ?? <span style={{ color: "var(--t-dim)" }}>unallocated</span>}</td>
                    <td className="mono muted">{c.company_id ?? "—"}</td>
                    <td className="num" style={{ textAlign: "right" }}>{c.count.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
